import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { PaginatedResultModel, SimilarHtsSuggestionModel } from '@app/data-layer/models/common';
import { FavoriteHtsNumberSearchModel } from '@app/data-layer/models/common/favorite-hts-number-search.model';
import { FavoriteHtsNumberModel } from '@app/data-layer/models/common/favorite-hts-number.model';
import { CompanyModel } from '@app/data-layer/models/companies';
import { BinaRepository, HtsChatRepository } from '@app/data-layer/repositories';
import { enumUserBetaFlag } from '@data-layer/enums/user';
import { CompaniesService } from '@services/companies.service';
import { enumBinaCatalogItemType } from '@enum/enumBinaCatalogItemType';
import { SearchHelper } from '@helpers/search-helper';
import { PaginationDynamicComponent } from '@shared/pagination/components';
import { Observable, of, ReplaySubject, Subject } from 'rxjs';
import { catchError, map, take, takeUntil } from 'rxjs/operators';
import { BinaCatalogSearchComponent } from '../bina-catalog-search/bina-catalog-search.component';

/**
 * "Find in BINA catalog" sidebar (#13411): opens with the company's whole saved list, the search box narrows it.
 * For tagged companies the search opens pre-filled with the line's description and, under the exact matches, lists the
 * similar products the company classified before (assistant-backed catalog items, #13517). Withdrawn codes stay listed
 * but cannot be inserted. `company` is set by the host right after the sidebar is created, before the first change detection.
 */
@Component({
  selector: 'app-bina-catalog-search-sidebar',
  templateUrl: './bina-catalog-search-sidebar.component.html',
  styleUrls: ['./bina-catalog-search-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BinaCatalogSearchSidebarComponent
  extends PaginationDynamicComponent<FavoriteHtsNumberModel, FavoriteHtsNumberSearchModel>
  implements OnInit, OnDestroy
{
  @Input() public company: CompanyModel;
  /** Pre-filled search (the line's description, tagged companies): the list opens already narrowed. */
  @Input() public initialSearch: string | null = null;
  @Output() public htsInsert: EventEmitter<FavoriteHtsNumberModel> = new EventEmitter<FavoriteHtsNumberModel>();
  @Output() public suggestionInsert: EventEmitter<SimilarHtsSuggestionModel> = new EventEmitter<SimilarHtsSuggestionModel>();
  @Output() public close: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild(BinaCatalogSearchComponent, { static: false })
  public searchBox: BinaCatalogSearchComponent;

  public enumBinaCatalogItemType: typeof enumBinaCatalogItemType = enumBinaCatalogItemType;
  public prefilledSearch: string | null = null;
  public suggestions: SimilarHtsSuggestionModel[] = [];

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();
  /** A newer search supersedes the suggestions request in flight. */
  private cancelSuggestions$: Subject<void> = new Subject<void>();

  constructor(
    private binaRepository: BinaRepository,
    private htsChatRepository: HtsChatRepository,
    private companiesService: CompaniesService,
    private cdr: ChangeDetectorRef,
  ) {
    super();
  }

  public ngOnInit(): void {
    // Load the list right away; the base ngOnInit would search without the company and is intentionally not called.
    if (this.isShowSearch) {
      this.prefilledSearch = this.isHtsAssistantEnabled ? (this.initialSearch || '').trim() || null : null;
      this.onSearch({ companyId: this.company.id, searchParameter: this.prefilledSearch });
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cancelSuggestions$.complete();
  }

  public get isShowSearch(): boolean {
    return !!this.company && !!this.company.id;
  }

  /** #13411: only companies with the classification assistant tag see which items carry a conversation. */
  public get isHtsAssistantEnabled(): boolean {
    return this.companiesService.hasUserBetaFlag(enumUserBetaFlag.htsClassificationAssistant);
  }

  public get isSearchApplied(): boolean {
    return !!this.lastSearch && !!this.lastSearch.searchParameter;
  }

  public get emptyStateKey(): string {
    return this.isSearchApplied ? 'BINA_CATALOG.SEARCH_EMPTY_STATE' : 'BINA_CATALOG.EMPTY_STATE';
  }

  public trackById(index: number, favoriteHts: FavoriteHtsNumberModel): number | string {
    return favoriteHts.id || favoriteHts.htsNumber;
  }

  public trackBySuggestion(index: number, suggestion: SimilarHtsSuggestionModel): string {
    return `${suggestion.htsNumber}-${suggestion.source}`;
  }

  /** Exact matches from the catalog (backend search) plus, for a non-empty text, the similar products. */
  public onSearch(filters: FavoriteHtsNumberSearchModel): void {
    this.search(filters);
    this.loadSuggestions(filters ? filters.searchParameter : null);
  }

  /** "Search all saved catalog HTS codes": clears the search — the whole list is back, without similar products. */
  public showAll(): void {
    if (this.searchBox) {
      this.searchBox.clearFilters();
      return;
    }
    this.onSearch({ companyId: this.company.id, searchParameter: null });
  }

  protected searchMethod(filters: FavoriteHtsNumberSearchModel): Observable<PaginatedResultModel<FavoriteHtsNumberModel>> {
    // The saved list is short and the sidebar has no pager: fetch everything and scroll. Tagged companies see withdrawn
    // codes (marked, not insertable); for everyone else the list stays as it was — active codes only.
    filters.limit = null;
    return this.binaRepository
      .searchFavoriteHtsNumbers(SearchHelper.convertFiltersToParams(filters), true)
      .pipe(
        map((response: PaginatedResultModel<FavoriteHtsNumberModel>) =>
          this.isHtsAssistantEnabled
            ? response
            : { ...response, items: response.items.filter((fhts: FavoriteHtsNumberModel) => fhts.isActive) },
        ),
      );
  }

  private loadSuggestions(text: string | null): void {
    const trimmed: string = (text || '').trim();
    this.suggestions = [];
    this.cancelSuggestions$.next();
    if (!this.isHtsAssistantEnabled || !trimmed) {
      this.cdr.markForCheck();
      return;
    }
    this.htsChatRepository
      .searchCompanySuggestions(this.company.id, trimmed)
      .pipe(
        catchError(() => of([] as SimilarHtsSuggestionModel[])),
        take(1),
        takeUntil(this.cancelSuggestions$),
        takeUntil(this.destroy$),
      )
      .subscribe((suggestions: SimilarHtsSuggestionModel[]) => {
        this.suggestions = suggestions || [];
        this.cdr.markForCheck();
      });
  }
}
